import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Badge,
  Modal,
  Typography,
  Tooltip,
  message,
  Popover,
  Drawer,
} from "antd";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  LayoutDashboard,
  MonitorPlay,
  FolderOpen,
  ListMusic,
  Crown,
  LogOut,
  HelpCircle,
  HardDrive,
  User,
  Infinity,
  LayoutTemplate,
  CalendarCheck,
  AppWindow,
  Users,
  Menu as MenuIcon,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import Overview from "./views/Overview";
import ScreenManager from "./views/ScreenManager";
import ContentLibrary from "./views/ContentLibrary";
import LoopPlaylist from "./views/LoopPlaylist";
import UserProfileModal from "./components/UserProfileModal";
import SetupOverlay from "./components/SetupOverlay";
import AIChatBot from "./components/AIChatBot";
import GuidedTour from "./components/GuidedTour";
import LiteAPI from "./api/liteAPI";
import {
  LanguageProvider,
  LangToggle,
  useLanguage,
  UI_TRANSLATIONS,
} from "./LanguageContext";
import "./lite.css";
import { useHistory, useParams } from "react-router-dom";
import { connect } from "react-redux";
import {
  requestServerGet,
  requestServerPost,
  requestFilesServerPost,
  requestNodeServerPost,
} from "../../actions";
import { MOCK_BRAND, MOCK_BRANCH, SCREEN_RATIOS } from "./mockData";

const { Text } = Typography;

const MENU_ICONS = [
  { key: "overview", icon: LayoutDashboard },
  { key: "screen", icon: MonitorPlay },
  { key: "library", icon: FolderOpen },
  { key: "playlist", icon: ListMusic },
];

const SidebarNav = ({
  activeTab,
  onNavigate,
  onProUpgrade,
  onLogout,
  collapsed = false,
  onToggleCollapse,
}) => {
  const { lang } = useLanguage();
  const t = UI_TRANSLATIONS[lang];
  return (
    <>
      {/* Main nav — padded like Pro's Row container */}
      <div style={{ padding: "12px 8px", width: "100%", flex: 1 }}>
        {MENU_ICONS.map(({ key, icon: Icon }) => (
          <Tooltip
            key={key}
            title={collapsed ? t.nav[key] : ""}
            placement="right"
          >
            <div
              id={`lite-nav-${key}`}
              className={`lite-side-btn ${activeTab === key ? "active" : ""}`}
              onClick={() => onNavigate(key)}
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon size={20} />
              </div>
              {!collapsed && <span>{t.nav[key]}</span>}
            </div>
          </Tooltip>
        ))}
      </div>

      {/* Bottom section — borderTop separator, like Pro */}
      <div
        style={{
          padding: "12px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
          flexShrink: 0,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          width: "100%",
        }}
      >
        <Tooltip title={collapsed ? t.upgradeTooltip : ""} placement="right">
          <div
            id="lite-nav-pro"
            className="lite-side-btn pro"
            onClick={onProUpgrade}
          >
            <div
              style={{
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Crown size={20} />
            </div>
            {!collapsed && <span>{t.upgradePro || "Pro"}</span>}
          </div>
        </Tooltip>
        <Tooltip title={collapsed ? t.help : ""} placement="right">
          <div
            id="lite-nav-help"
            className="lite-side-btn"
            onClick={() => window.__startLiteTour && window.__startLiteTour()}
          >
            <div
              style={{
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <HelpCircle size={20} />
            </div>
            {!collapsed && <span>{t.help || "Help"}</span>}
          </div>
        </Tooltip>
        <Tooltip title={collapsed ? t.signOut : ""} placement="right">
          <div
            className="lite-side-btn lite-side-btn--danger"
            onClick={onLogout}
          >
            <div
              style={{
                width: 20,
                height: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LogOut size={20} />
            </div>
            {!collapsed && <span>{t.signOut}</span>}
          </div>
        </Tooltip>
        {onToggleCollapse && (
          <Tooltip title={collapsed ? "Expand" : ""} placement="right">
            <div
              className="lite-sidebar-collapse-btn"
              onClick={onToggleCollapse}
            >
              {collapsed ? (
                <PanelLeftOpen size={16} />
              ) : (
                <>
                  <PanelLeftClose size={16} />
                  <span>Collapse</span>
                </>
              )}
            </div>
          </Tooltip>
        )}
      </div>
    </>
  );
};

const PageTitleText = ({
  activeTab,
  hasScreen,
  allScreens,
  libraryList,
  playlistItems,
}) => {
  const { lang } = useLanguage();
  const pt = UI_TRANSLATIONS[lang].pageTitle;
  switch (activeTab) {
    case "overview":
      return pt.overview;
    case "screen":
      return pt.screen(hasScreen, allScreens.length);
    case "library":
      return pt.library(libraryList.length);
    case "playlist":
      return pt.playlist(playlistItems.length);
    default:
      return pt.overview;
  }
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
};

const TopBarStats = ({
  allScreens,
  inPopover = false,
  libraryList = [],
  accountStorage = null,
}) => {
  const { lang } = useLanguage();
  const t = UI_TRANSLATIONS[lang].topbar;
  const storageUsed = libraryList.reduce(
    (sum, f) => sum + (Number(f.sizeBytes) || 0),
    0,
  );
  const storageTotal = accountStorage?.storagebyte?.totalspace ?? null;
  const storageLabel = storageTotal
    ? `${formatBytes(storageUsed)} / ${formatBytes(storageTotal)}`
    : formatBytes(storageUsed);
  return (
    <div
      style={{
        display: "flex",
        alignItems: inPopover ? "stretch" : "center",
        gap: 8,
        flexDirection: "row",
      }}
    >
      {/* Screens pill */}
      <div
        title={`${t.screens}: ${allScreens.length} / 1`}
        style={{
          display: "flex",
          alignItems: inPopover ? "stretch" : "center",
          flexDirection: inPopover ? "column" : "row",
          gap: 5,
          padding: inPopover ? "7px 10px" : "3px 10px",
          borderRadius: inPopover ? 10 : 20,
          background: "rgba(99,102,241,0.08)",
          border: "1px solid rgba(99,102,241,0.18)",
          width: inPopover ? "calc(50% - 4px)" : "auto",
          justifyContent: "flex-start",
          minWidth: 0,
        }}
      >
        {inPopover ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <MonitorPlay size={14} color="#6366f1" />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                  whiteSpace: "nowrap",
                  lineHeight: 1.1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {allScreens.length} / 1
              </span>
            </div>
            <span
              style={{
                fontSize: 10,
                color: "#94a3b8",
                letterSpacing: "0.04em",
                marginLeft: 19,
                lineHeight: 1,
              }}
            >
              {t.screens}
            </span>
          </>
        ) : (
          <>
            <MonitorPlay size={14} color="#6366f1" />
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#334155",
                whiteSpace: "nowrap",
              }}
            >
              {allScreens.length} / 1
            </span>
            <span
              style={{
                fontSize: 10,
                color: "#94a3b8",
                letterSpacing: "0.04em",
              }}
            >
              {t.screens}
            </span>
          </>
        )}
      </div>

      {/* Storage pill */}
      <div
        title={`${t.storage}: 424 MB / 100 GB`}
        style={{
          display: "flex",
          alignItems: inPopover ? "stretch" : "center",
          flexDirection: inPopover ? "column" : "row",
          gap: 5,
          padding: inPopover ? "7px 10px" : "3px 10px",
          borderRadius: inPopover ? 10 : 20,
          background: "rgba(99,102,241,0.08)",
          border: "1px solid rgba(99,102,241,0.18)",
          width: inPopover ? "calc(50% - 4px)" : "auto",
          justifyContent: "flex-start",
          minWidth: 0,
        }}
      >
        {inPopover ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <HardDrive size={14} color="#6366f1" />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                  whiteSpace: "nowrap",
                  lineHeight: 1.1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {storageLabel}
              </span>
            </div>
            <span
              style={{
                fontSize: 10,
                color: "#94a3b8",
                letterSpacing: "0.04em",
                marginLeft: 19,
                lineHeight: 1,
              }}
            >
              {t.storage}
            </span>
          </>
        ) : (
          <>
            <HardDrive size={14} color="#6366f1" />
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "#334155",
                whiteSpace: "nowrap",
              }}
            >
              {storageLabel}
            </span>
            <span
              style={{
                fontSize: 10,
                color: "#94a3b8",
                letterSpacing: "0.04em",
              }}
            >
              {t.storage}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const FEATURE_ICONS = [
  Infinity,
  LayoutTemplate,
  CalendarCheck,
  AppWindow,
  Users,
];

const ProUpgradeModal = ({ open, onClose, usertoken, requestServerPost }) => {
  const { lang } = useLanguage();
  const t = (UI_TRANSLATIONS[lang] || UI_TRANSLATIONS.en).proModal;
  const [form, setForm] = useState({
    email: "",
    company: "",
    screens: "",
    phone: "",
    needs: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = () => {
    if (!form.email.trim()) {
      message.warning(t.warnEmail);
      return;
    }
    if (!form.company.trim()) {
      message.warning("Please enter your company name");
      return;
    }
    if (!form.screens.trim()) {
      message.warning("Please enter the number of screens");
      return;
    }
    if (!form.phone.trim()) {
      message.warning("Please enter your phone number");
      return;
    }
    if (!form.needs.trim()) {
      message.warning("Please tell us about your needs");
      return;
    }
    setSending(true);
    requestServerPost(
      "submit_pro_request_form",
      {
        email: form.email,
        company: form.company,
        screens: form.screens,
        phone: form.phone,
        needs: form.needs,
      },
      {},
      (response) => {
        setSending(false);
        if (response?.data?.status === "success") {
          onClose();
          setForm({ email: "", company: "", screens: "", phone: "", needs: "" });
          message.success(t.successMsg);
        } else {
          message.error(
            response?.data?.message || "Failed to submit request",
          );
        }
      },
      usertoken,
      {},
      () => {
        setSending(false);
        message.error("Failed to submit request");
      },
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(860px, 95vw)"
      centered
      destroyOnClose
      bodyStyle={{ padding: 0 }}
      style={{ borderRadius: 16, overflow: "hidden", padding: 0 }}
      styles={{ content: { padding: 0, borderRadius: 16, overflow: "hidden" } }}
    >
      <div className="pro-modal-body">
        {/* Left panel */}
        <div className="pro-modal-left">
          <div
            style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          >
            <div
              style={{
                position: "absolute",
                right: -60,
                top: "25%",
                width: 220,
                height: 220,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(139,92,246,0.55) 0%, rgba(109,40,217,0.25) 50%, transparent 75%)",
              }}
            />
            <div
              style={{
                position: "absolute",
                right: -100,
                bottom: "10%",
                width: 260,
                height: 260,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(109,40,217,0.4) 0%, rgba(79,26,180,0.15) 55%, transparent 75%)",
              }}
            />
          </div>
          <div
            style={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 24,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Crown size={22} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>
                  {t.headline1}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>
                  {t.headline2}
                </div>
              </div>
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: "rgba(255,255,255,0.18)",
                borderRadius: 20,
                padding: "4px 14px",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 1,
                width: "fit-content",
              }}
            >
              {t.badge}
            </div>
            {FEATURE_ICONS.map((Icon, i) => {
              const feat = t.features[i];
              return (
                <div
                  key={i}
                  style={{ display: "flex", gap: 14, alignItems: "flex-start" }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: "rgba(255,255,255,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={20} color="#fff" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {feat.title}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
                      {feat.desc}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right panel */}
        <div className="pro-modal-right">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: "#ede9fe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: 22 }}>🎧</span>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>
                {t.salesTitle}
              </div>
              <div style={{ fontSize: 13, color: "#64748b" }}>
                {t.salesDesc}
              </div>
            </div>
          </div>

          {[
            {
              label: t.labelEmail,
              key: "email",
              placeholder: t.placeholderEmail,
              type: "email",
              icon: "✉️",
            },
            {
              label: t.labelCompany,
              key: "company",
              placeholder: t.placeholderCompany,
              icon: "🏢",
            },
            {
              label: t.labelScreens,
              key: "screens",
              placeholder: t.placeholderScreens,
              icon: "🖥",
            },
          ].map(({ label, key, placeholder, type, icon }) => (
            <div key={key}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#374151",
                  marginBottom: 6,
                }}
              >
                {label} <span style={{ color: "#ef4444" }}>*</span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              >
                <span style={{ fontSize: 16 }}>{icon}</span>
                <input
                  required
                  type={type || "text"}
                  placeholder={placeholder}
                  value={form[key]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm((p) => ({ ...p, [key]: value }));
                  }}
                  style={{
                    border: "none",
                    outline: "none",
                    flex: 1,
                    fontSize: 14,
                    color: "#0f172a",
                    background: "transparent",
                  }}
                />
              </div>
            </div>
          ))}

          {/* Phone field */}
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#374151",
                marginBottom: 6,
              }}
            >
              {t.labelPhone} <span style={{ color: "#ef4444" }}>*</span>
            </div>
            <div
              className="pro-phone-wrap"
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: "6px 12px",
              }}
            >
              <PhoneInput
                international
                defaultCountry="TH"
                placeholder={t.placeholderPhone}
                value={form.phone}
                onChange={(val) => setForm((p) => ({ ...p, phone: val || "" }))}
                style={{ fontSize: 14 }}
              />
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#374151",
                marginBottom: 6,
              }}
            >
              {t.labelNeeds} <span style={{ color: "#ef4444" }}>*</span>
            </div>
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                padding: "8px 12px",
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <span style={{ fontSize: 16, marginTop: 2 }}>✏️</span>
              <textarea
                required
                rows={3}
                placeholder={t.placeholderNeeds}
                value={form.needs}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((p) => ({ ...p, needs: value }));
                }}
                style={{
                  border: "none",
                  outline: "none",
                  flex: 1,
                  fontSize: 14,
                  color: "#0f172a",
                  background: "transparent",
                  resize: "none",
                  fontFamily: "inherit",
                }}
              />
            </div>
          </div>

          <button
            disabled={sending}
            onClick={handleSubmit}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: 10,
              border: "none",
              background: sending ? "#818cf8" : "#4f46e5",
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              cursor: sending ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {sending ? t.btnSending : `✈ ${t.btnContact}`}
          </button>
          <div style={{ textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
            🔒 {t.secureNote}
          </div>
        </div>
      </div>
    </Modal>
  );
};

// Rendered inside <LanguageProvider> so useLanguage() gets the user's chosen lang
const TourHelpButton = ({ activeTab, onNavigate, isReady }) => {
  const tour = GuidedTour({ activeTab, onNavigate, isReady });
  // Expose the tour so the sidebar Help item can trigger it (mirrors Pro's window.__startProTour)
  useEffect(() => {
    window.__startLiteTour = tour.startTour;
    return () => {
      delete window.__startLiteTour;
    };
  }, [tour.startTour]);
  return (
    <Tooltip title="Product Tour" placement="left">
      <div className="lite-help-btn" onClick={() => tour.startTour()}>
        <HelpCircle size={22} />
      </div>
    </Tooltip>
  );
};

const LiteDashboard = ({
  usertoken,
  userdata,
  selectedBrandData,
  requestServerGet,
  requestServerPost,
  requestFilesServerPost,
  requestNodeServerPost,
}) => {
  const { brandid, branchid, menu } = useParams();
  const activeTab = menu || "overview";
  const history = useHistory();
  const { lang } = useLanguage();
  const tMenu = UI_TRANSLATIONS[lang] || UI_TRANSLATIONS.en;

  // Sidebar responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [sidebarDrawerOpen, setSidebarDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Setup state
  const [isSettingUp, setIsSettingUp] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [proModalOpen, setProModalOpen] = useState(false);

  // Brand / Branch
  const [brandData, setBrandData] = useState(null);
  const [branchData, setBranchData] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [branchName, setBranchName] = useState("");

  // Screen state — up to 2 screens are fully active
  const [hasScreen, setHasScreen] = useState(false);
  const [screenData, setScreenData] = useState(null);
  const [allScreens, setAllScreens] = useState([]); // all screens (up to 2)
  const [selectedRatio, setSelectedRatio] = useState(SCREEN_RATIOS[0]);

  // Content Library (all uploaded media)
  const [libraryList, setLibraryList] = useState([]);
  const [accountStorage, setAccountStorage] = useState(null);

  // Per-screen playlists: { [screenId]: { id: string|null, items: array } }
  const [screenPlaylists, setScreenPlaylists] = useState({});
  // Which screen's playlist is currently shown in LoopPlaylist
  const [activePlaylistScreenId, setActivePlaylistScreenId] = useState(null);

  // Derive current playlist values from the active screen
  const _activeEntry = screenPlaylists[activePlaylistScreenId] ?? {
    id: null,
    items: [],
  };
  const playlistId = _activeEntry.id;
  const playlistItems = _activeEntry.items;

  // Stable ref so setPlaylistItems callback never needs to be recreated
  const activePlaylistScreenIdRef = useRef(null);
  activePlaylistScreenIdRef.current = activePlaylistScreenId;

  // Drop-in replacement for the old setPlaylistItems — updates only the active screen
  const setPlaylistItems = useCallback(
    (updaterOrVal) => {
      setScreenPlaylists((prev) => {
        const sid = activePlaylistScreenIdRef.current;
        if (!sid) return prev;
        const cur = prev[sid] ?? { id: null, items: [] };
        const newItems =
          typeof updaterOrVal === "function"
            ? updaterOrVal(cur.items)
            : updaterOrVal;
        return { ...prev, [sid]: { ...cur, items: newItems } };
      });
    },
    [], // stable — reads screenId from ref
  );

  // Tracks whether the last action was an explicit unpair.
  // Used to distinguish: logout→login (restore playlist) vs unpair→re-pair (start fresh).
  const afterUnpairRef = useRef(false);

  // Props bundle for passing to LiteAPI calls
  const apiProps = {
    usertoken,
    requestServerGet,
    requestServerPost,
    requestFilesServerPost,
    requestNodeServerPost,
    selectedBrandData,
  };

  // ─── Load real data on mount ─────────────────────────────────
  const loadUserData = useCallback(async () => {
    try {
      // 1. Get user's brands — auto-select first
      const brands = await LiteAPI.getBrands(apiProps);
      if (!brands || brands.length === 0) throw new Error("No brands found");

      const brand = brands[0];
      setBrandData(brand);
      setBrandName(brand.BrandName || "My Workspace");

      // 2. Get branches for this brand — auto-select first
      const branches = await LiteAPI.getBranches(brand.BrandID, apiProps);
      if (!branches || branches.length === 0)
        throw new Error("No branches found");

      const branch = branches[0];
      setBranchData(branch);
      setBranchName(branch.BranchName || "Main Location");

      // Update URL to reflect real brand/branch IDs
      const desiredPath = `/lite/${brand.BrandID}/${branch.BranchID}/${activeTab}`;
      if (history.location.pathname !== desiredPath) {
        history.replace(desiredPath);
      }

      // 3. Load files for library
      const files = await LiteAPI.getFiles(brand.BrandID, apiProps);
      setLibraryList(files || []);

      // 3b. Load storage quota (non-blocking)
      LiteAPI.getAccountStorage(brand.BrandID, usertoken)
        .then((data) => {
          if (data) setAccountStorage(data);
        })
        .catch(() => {});

      // 4. Load screens — up to 2 are fully active
      let loadedScreens = [];
      try {
        const screens = await LiteAPI.getScreens(
          brand.BrandID,
          branch.BranchID,
          apiProps,
        );
        if (screens && screens.length > 0) {
          loadedScreens = screens;
          setAllScreens(screens);
          setHasScreen(true);
          setScreenData(screens[0]);
        }
      } catch (e) {
        console.log("Screen loading skipped:", e);
      }

      // 5. Resolve playlists for each screen (up to 2)
      if (loadedScreens.length > 0) {
        // Fetch all playlists and campaigns once to avoid multiple round-trips
        const [allPlaylists, allCampaigns] = await Promise.all([
          LiteAPI.getPlaylists(brand.BrandID, apiProps),
          LiteAPI.getCampaigns(brand.BrandID, apiProps),
        ]);
        const playlistMap = {};

        for (const screen of loadedScreens.slice(0, 2)) {
          const resolution = await LiteAPI.resolvePlaylistForScreen(
            screen,
            brand.BrandID,
            apiProps,
          );
          const pid = resolution?.playlistId ?? null;
          const cid = resolution?.campaignId ?? screen.raw?.CampaignID ?? null;
          const matched = pid
            ? allPlaylists.find((p) => `${p.id}` === `${pid}`)
            : null;
          const matchedCampaign = cid
            ? allCampaigns.find((c) => `${c.CampaignID}` === `${cid}`)
            : null;
          playlistMap[screen.id] = {
            id: pid,
            items: matched?.items || [],
            campaignName: matchedCampaign?.CampaignName || null,
          };
          console.log(`[Lite] Screen ${screen.id} → playlist ${pid ?? "none"}`);
        }

        setScreenPlaylists(playlistMap);
        setActivePlaylistScreenId(loadedScreens[0].id);
      } else {
        // No screens yet — try to load the first available playlist as a fallback
        const playlists = await LiteAPI.getPlaylists(brand.BrandID, apiProps);
        if (playlists && playlists.length > 0) {
          const playlist = playlists[0];
          // Use a placeholder key so UI still works before a screen is paired
          setScreenPlaylists({
            _unassigned: { id: playlist.id, items: playlist.items || [] },
          });
          setActivePlaylistScreenId("_unassigned");
          console.log(
            "[Lite] No screens — using first available playlist:",
            playlist.id,
          );
        }
      }
    } catch (error) {
      console.error("Failed to load user data, using fallback:", error);
      // Fallback to mock data if APIs aren't available yet
      setBrandName(MOCK_BRAND.BrandName);
      setBranchName(MOCK_BRANCH.BranchName);
      const desiredPath = `/lite/${MOCK_BRAND.BrandID}/${MOCK_BRANCH.BranchID}/${activeTab}`;
      if (history.location.pathname !== desiredPath) {
        history.replace(desiredPath);
      }
    } finally {
      setIsSettingUp(false);
    }
  }, [usertoken]);

  useEffect(() => {
    if (!usertoken) {
      history.replace("/lite/login");
      return;
    }
    loadUserData();
  }, [loadUserData]);

  // Refresh library data from server
  const refreshLibrary = useCallback(async () => {
    if (!brandData) return;
    const files = await LiteAPI.getFiles(brandData.BrandID, apiProps);
    setLibraryList(files || []);
  }, [brandData, usertoken]);

  // Refresh playlist data for the currently active screen.
  // Blocked after an unpair (afterUnpairRef=true) so stale server-side items
  // don't come back. The flag is cleared once a new screen is paired.
  const refreshPlaylist = useCallback(async () => {
    const sid = activePlaylistScreenIdRef.current;
    if (!brandData || !sid || afterUnpairRef.current) return;
    const currentPid = screenPlaylists[sid]?.id;
    if (!currentPid) return;
    const playlists = await LiteAPI.getPlaylists(brandData.BrandID, apiProps);
    if (!playlists || playlists.length === 0) return;
    const target = playlists.find((p) => `${p.id}` === `${currentPid}`);
    if (!target) return;
    setScreenPlaylists((prev) => {
      const localItems = prev[sid]?.items || [];
      // Preserve the React key of any locally-pending item once its
      // confirmed server row shows up here, matched by the source file.
      // Without this, replacing "pending-..." with the server's MediaID
      // swaps the list key for that row, and AnimatePresence/layout
      // animations can briefly render both the old and new node at once —
      // showing the same item twice until the next full reload.
      const usedLocalIds = new Set();
      const reconciled = (target.items || []).map((serverItem) => {
        const matchLocal = localItems.find(
          (li) =>
            !usedLocalIds.has(li.id) &&
            String(li.id).startsWith("pending-") &&
            li.raw?.FileID != null &&
            `${li.raw.FileID}` === `${serverItem.fileId}`,
        );
        if (matchLocal) {
          usedLocalIds.add(matchLocal.id);
          return { ...serverItem, id: matchLocal.id };
        }
        return serverItem;
      });
      return { ...prev, [sid]: { ...prev[sid], items: reconciled } };
    });
  }, [brandData, screenPlaylists, usertoken]);

  // Called by ScreenManager after a screen is successfully paired.
  // Resolves the playlist created by create_screen_lite and stores it per screen.
  const handleScreenPaired = useCallback(
    async (rawScreen) => {
      if (!brandData) return;
      const screenId = rawScreen.ScreenID;
      // Clear this screen's playlist immediately on new pairing
      setScreenPlaylists((prev) => ({
        ...prev,
        [screenId]: { id: null, items: [] },
      }));
      setActivePlaylistScreenId(screenId);
      try {
        const resolution = await LiteAPI.resolvePlaylistForScreen(
          { raw: rawScreen },
          brandData.BrandID,
          apiProps,
        );
        if (resolution?.playlistId) {
          // New screen has a brand-new empty playlist — keep items empty.
          setScreenPlaylists((prev) => ({
            ...prev,
            [screenId]: { id: resolution.playlistId, items: [] },
          }));
        }
      } catch (e) {
        console.warn("[Lite] Could not resolve playlist after pairing:", e);
      } finally {
        afterUnpairRef.current = false;
      }
    },
    [brandData, usertoken],
  );

  // Refresh screen data from server
  const refreshScreens = useCallback(async () => {
    if (!brandData || !branchData) return;
    try {
      const screens = await LiteAPI.getScreens(
        brandData.BrandID,
        branchData.BranchID,
        apiProps,
      );
      setAllScreens(screens || []);
      if (screens && screens.length > 0) {
        setHasScreen(true);
        setScreenData(screens[0]);
      } else {
        setHasScreen(false);
        setScreenData(null);
      }
    } catch (e) {
      console.log("Screen refresh error:", e);
    }
  }, [brandData, branchData, usertoken]);

  const handleNavigate = (newTab) => {
    const bId = brandid || MOCK_BRAND.BrandID;
    const brId = branchid || MOCK_BRANCH.BranchID;
    history.push(`/lite/${bId}/${brId}/${newTab}`);
  };

  const handleLogout = () => {
    history.push("/lite/login");
  };

  const handleProUpgrade = () => setProModalOpen(true);

  const userSettingModule = () => {
    const { user = {} } = userdata || {};
    const role = user.role || user.usertype || "";
    const roleLabel = Array.isArray(role) ? role[0] : role;
    const username = user.username || user.email || "User";

    const menuItem = (icon, label, onClick, danger = false) => (
      <div
        onClick={onClick}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "9px 14px",
          cursor: "pointer",
          borderRadius: 8,
          color: danger ? "#ef4444" : "#334155",
          fontSize: 14,
          fontWeight: 500,
          transition: "background 0.12s",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = danger ? "#fef2f2" : "#f1f5f9")
        }
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span
          style={{
            display: "flex",
            color: danger ? "#ef4444" : "#6366f1",
            flexShrink: 0,
          }}
        >
          {icon}
        </span>
        {label}
      </div>
    );

    return (
      <div style={{ width: 320, padding: "4px 0" }}>
        <div
          style={{
            padding: "12px 14px 10px",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "linear-gradient(135deg,#6366f1,#4f46e5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <User size={17} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0f172a",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {username}
            </div>
            {roleLabel ? (
              <div
                style={{
                  display: "inline-block",
                  marginTop: 3,
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "1px 7px",
                  borderRadius: 20,
                  background: "linear-gradient(135deg,#eef2ff,#e0e7ff)",
                  color: "#6366f1",
                  letterSpacing: "0.04em",
                }}
              >
                {Array.isArray(role) ? role.join(", ") : role}
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ padding: "4px 12px 8px" }}>
          <TopBarStats
            allScreens={allScreens}
            inPopover={true}
            libraryList={libraryList}
            accountStorage={accountStorage}
          />
        </div>

        <div style={{ height: 1, background: "#f1f5f9", margin: "4px 0" }} />
        <div style={{ padding: "4px 6px" }}>
          {menuItem(<User size={16} />, tMenu.userProfile.editProfile, () => {
            setProfileMenuOpen(false);
            setShowProfileModal(true);
          })}
          {menuItem(
            <LogOut size={16} />,
            tMenu.signOut,
            () => {
              setProfileMenuOpen(false);
              handleLogout();
            },
            true,
          )}
        </div>
      </div>
    );
  };

  if (isSettingUp) {
    return <SetupOverlay />;
  }

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return (
          <Overview
            onNavigate={handleNavigate}
            hasScreen={hasScreen}
            screenData={screenData}
            libraryCount={libraryList.length}
            playlistCount={Object.values(screenPlaylists).reduce(
              (sum, p) => sum + (p.items?.length || 0),
              0,
            )}
            apiProps={apiProps}
            brandData={brandData}
          />
        );
      case "screen":
        return (
          <ScreenManager
            hasScreen={hasScreen}
            setHasScreen={setHasScreen}
            screenData={screenData}
            setScreenData={setScreenData}
            allScreens={allScreens}
            selectedRatio={selectedRatio}
            setSelectedRatio={setSelectedRatio}
            ratios={SCREEN_RATIOS}
            screenPlaylists={screenPlaylists}
            playlistItems={playlistItems}
            setPlaylistItems={setPlaylistItems}
            onUnpaired={(unpairedScreenId) => {
              setScreenPlaylists((prev) => {
                const next = { ...prev };
                delete next[unpairedScreenId];
                return next;
              });
              // Switch active playlist to another remaining screen
              const remaining = allScreens.filter(
                (s) => s.id !== unpairedScreenId,
              );
              setActivePlaylistScreenId(remaining[0]?.id ?? null);
              afterUnpairRef.current = true;
            }}
            onNavigate={handleNavigate}
            apiProps={apiProps}
            brandData={brandData}
            branchData={branchData}
            refreshScreens={refreshScreens}
            onScreenPaired={handleScreenPaired}
            onProUpgrade={handleProUpgrade}
          />
        );
      case "library":
        return (
          <ContentLibrary
            libraryList={libraryList}
            setLibraryList={setLibraryList}
            playlistItems={playlistItems}
            setPlaylistItems={setPlaylistItems}
            apiProps={apiProps}
            brandData={brandData}
            playlistId={playlistId}
            refreshLibrary={refreshLibrary}
            refreshPlaylist={refreshPlaylist}
          />
        );
      case "playlist":
        return (
          <LoopPlaylist
            playlistItems={playlistItems}
            setPlaylistItems={setPlaylistItems}
            libraryList={libraryList}
            setLibraryList={setLibraryList}
            onNavigate={handleNavigate}
            hasScreen={hasScreen}
            selectedRatio={selectedRatio}
            apiProps={apiProps}
            brandData={brandData}
            playlistId={playlistId}
            screenData={
              allScreens.find((s) => s.id === activePlaylistScreenId) ??
              screenData
            }
            refreshPlaylist={refreshPlaylist}
            refreshLibrary={refreshLibrary}
            allScreens={allScreens}
            activePlaylistScreenId={activePlaylistScreenId}
            onSwitchPlaylistScreen={setActivePlaylistScreenId}
          />
        );
      default:
        return (
          <Overview
            onNavigate={handleNavigate}
            hasScreen={hasScreen}
            screenData={screenData}
            libraryCount={libraryList.length}
            playlistCount={Object.values(screenPlaylists).reduce(
              (sum, p) => sum + (p.items?.length || 0),
              0,
            )}
            apiProps={apiProps}
            brandData={brandData}
          />
        );
    }
  };

  return (
    <LanguageProvider>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
        }}
        className="lite-layout"
      >
        {/* ── Top Bar (full width) ── */}
        <div className="lite-topbar" id="lite-topbar" style={{ flexShrink: 0 }}>
          {/* Left: hamburger (responsive only) + Logo + App name + LITE badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? "8px" : "12px",
            }}
          >
            {isMobile && (
              <div
                onClick={() => setSidebarDrawerOpen(true)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.8)",
                  flexShrink: 0,
                  background: "rgba(255,255,255,0.08)",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.14)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.08)")
                }
              >
                <MenuIcon size={18} />
              </div>
            )}
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                overflow: "hidden",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.08)",
              }}
            >
              <img
                src="/cms/assets/svgs/cdx_logo_blackandwhite.svg"
                alt="CodeX"
                style={{
                  width: 20,
                  height: 20,
                  objectFit: "contain",
                  opacity: 0.9,
                }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <span
                style={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.95)",
                  letterSpacing: "0.02em",
                  lineHeight: 1.2,
                }}
              >
                CodeX Signage
              </span>
              <span className="lite-topbar-subtitle">
                <PageTitleText
                  activeTab={activeTab}
                  hasScreen={hasScreen}
                  allScreens={allScreens}
                  libraryList={libraryList}
                  playlistItems={playlistItems}
                />
              </span>
            </div>
            <span className="lite-version-badge">LITE</span>
          </div>

          {/* Right: controls */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? "8px" : "16px",
            }}
          >
            <LangToggle compact={isMobile} />
            <Popover
              trigger="click"
              placement="bottomRight"
              title={null}
              visible={profileMenuOpen}
              onVisibleChange={setProfileMenuOpen}
              overlayInnerStyle={{
                padding: 0,
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              }}
              content={userSettingModule}
            >
              <Tooltip title="Account" placement="bottom">
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#fff",
                    cursor: "pointer",
                    flexShrink: 0,
                    boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
                    userSelect: "none",
                  }}
                >
                  {(userdata?.user?.username || userdata?.user?.email || "U")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
              </Tooltip>
            </Popover>
          </div>
        </div>

        {/* ── Body: Sidebar + Content ── */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* ── Sidebar (desktop) ── */}
          {!isMobile && (
            <div
              className={`lite-sidebar ${sidebarCollapsed ? "lite-sidebar--collapsed" : ""}`}
              id="lite-sidebar"
            >
              <SidebarNav
                activeTab={activeTab}
                onNavigate={handleNavigate}
                onProUpgrade={handleProUpgrade}
                onLogout={handleLogout}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
              />
            </div>
          )}

          {/* ── Sidebar (mobile drawer) ── */}
          {isMobile && (
            <Drawer
              placement="left"
              closable={false}
              onClose={() => setSidebarDrawerOpen(false)}
              visible={sidebarDrawerOpen}
              bodyStyle={{ padding: 0, backgroundColor: "#1e293b" }}
              width={200}
            >
              <div
                className="lite-sidebar"
                style={{ width: "100%", height: "100%" }}
              >
                <SidebarNav
                  activeTab={activeTab}
                  onNavigate={(tab) => {
                    handleNavigate(tab);
                    setSidebarDrawerOpen(false);
                  }}
                  onProUpgrade={() => {
                    handleProUpgrade();
                    setSidebarDrawerOpen(false);
                  }}
                  onLogout={handleLogout}
                />
              </div>
            </Drawer>
          )}

          {/* ── Main Content ── */}
          <div
            style={{
              flex: 1,
              backgroundColor: "#f8fafc",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div className="lite-content-area">
              <div className="lite-content-inner">{renderContent()}</div>
            </div>
          </div>
        </div>

        {/* <AIChatBot /> */}

        {/* ── Upgrade to Pro Modal ── */}
        <ProUpgradeModal
          open={proModalOpen}
          onClose={() => setProModalOpen(false)}
          usertoken={usertoken}
          requestServerPost={requestServerPost}
        />

        <UserProfileModal
          visible={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          userdata={userdata}
          usertoken={usertoken}
          requestServerPost={requestServerPost}
        />
        <UserProfileModal
          visible={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          userdata={userdata}
          usertoken={usertoken}
          requestServerPost={requestServerPost}
        />

        {/* Floating Help Button — hidden for now (Help is available in the sidebar).
            Kept mounted so the tour still auto-plays on first visit & window.__startLiteTour stays wired. */}
        <div style={{ display: "none" }}>
          <TourHelpButton
            activeTab={activeTab}
            onNavigate={handleNavigate}
            isReady={!isSettingUp}
          />
        </div>
      </div>
    </LanguageProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    usertoken: state.usertoken,
    userdata: state.userdata,
    selectedBrandData: state.selectedBrandData,
  };
};

export default connect(mapStateToProps, {
  requestServerGet,
  requestServerPost,
  requestFilesServerPost,
  requestNodeServerPost,
})(LiteDashboard);
